// apps/client/src/components/auth/AppCheckRecoveryBanner.tsx
//
// Recovery UI shown on the login page when an auth failure is classified as
// an App Check rejection. Offers three actionable paths:
//   1. Reset & try again — wipe IndexedDB throttle state and reload /login
//   2. Open in incognito  — copy the login URL to clipboard with a hint
//   3. Contact support    — prefilled mailto with diagnostics in the body

import React, { useState } from 'react';
import { Shield, RefreshCw, Eye, Mail } from 'lucide-react';
import type { AppCheckErrorContext } from '../../lib/appCheckRecovery';

interface Props {
  ctx: AppCheckErrorContext;
  onReset: () => void | Promise<void>;
}

const SUPPORT_EMAIL = 'support@valutten.com';
const SUPPORT_SUBJECT = 'App Check login failure';

const buildSupportMailto = (ctx: AppCheckErrorContext): string => {
  const code =
    typeof ctx.raw === 'object' &&
    ctx.raw !== null &&
    typeof (ctx.raw as { code?: unknown }).code === 'string'
      ? ((ctx.raw as { code: string }).code)
      : '(unknown)';
  const userAgent =
    typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
      ? navigator.userAgent
      : '(unknown)';
  const lines = [
    `Timestamp: ${new Date().toISOString()}`,
    `Error code: ${code}`,
    `User agent: ${userAgent}`,
    '',
    'Please help me log in.',
  ];
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    SUPPORT_SUBJECT
  )}&body=${encodeURIComponent(lines.join('\n'))}`;
};

const AppCheckRecoveryBanner: React.FC<Props> = ({ ctx, onReset }) => {
  const [resetting, setResetting] = useState<boolean>(false);
  const [copyHintVisible, setCopyHintVisible] = useState<boolean>(false);

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await onReset();
    } catch {
      // onReset is responsible for redirecting; if it throws there's no UI to update
      setResetting(false);
    }
  };

  const handleCopyIncognitoUrl = async () => {
    if (typeof window === 'undefined') return;
    const url = `${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyHintVisible(true);
      window.setTimeout(() => setCopyHintVisible(false), 4000);
    } catch {
      // Clipboard API unavailable or denied — still show the hint so the user
      // can manually type the URL.
      setCopyHintVisible(true);
      window.setTimeout(() => setCopyHintVisible(false), 4000);
    }
  };

  const mailtoHref = buildSupportMailto(ctx);

  return (
    <div
      className="bg-amber-900/40 border border-amber-500/60 text-amber-100 p-4 rounded-lg mb-6"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start mb-3">
        <Shield size={20} className="mr-3 flex-shrink-0 mt-0.5 text-amber-300" />
        <div>
          <h3 className="text-sm font-semibold text-amber-100 mb-1">
            Browser security check failed
          </h3>
          <p className="text-xs text-amber-200/90 leading-relaxed">
            Your browser session didn&rsquo;t pass our security check. This is
            usually caused by privacy extensions, DevTools being open, or signing
            into multiple accounts from the same browser.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <button
          type="button"
          onClick={handleReset}
          disabled={resetting}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <RefreshCw size={14} className={resetting ? 'animate-spin' : undefined} />
          <span>{resetting ? 'Resetting...' : 'Reset & try again'}</span>
        </button>

        <button
          type="button"
          onClick={handleCopyIncognitoUrl}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-gray-100 text-xs font-medium px-3 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          <Eye size={14} />
          <span>Open in incognito</span>
        </button>

        <a
          href={mailtoHref}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-gray-100 text-xs font-medium px-3 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          <Mail size={14} />
          <span>Contact support</span>
        </a>
      </div>

      {copyHintVisible && (
        <p className="mt-3 text-xs text-amber-200/90" role="status" aria-live="polite">
          URL copied &mdash; open Chrome &rarr; &#8984;&#8679;N &rarr; paste
        </p>
      )}
    </div>
  );
};

export default AppCheckRecoveryBanner;
